/**
 * pastoralExhaustionDetector.js
 *
 * Detects ministerial exhaustion from the PASTOR'S own language.
 *
 * DISTINCT FROM:
 *   silentTensionDetector (FASE 10) → hidden relational tension in ecclesial context
 *   emotionalCapacityEvaluator (FASE 11) → the PERSON being cared for
 *   ministryLoadBalancer (autonomy) → session usage fatigue (nav/message density)
 * THIS: the pastor's OWN interior exhaustion expressed in text.
 *
 * States (ordered by severity):
 *   estable               — no exhaustion signals
 *   cansancio_temporal    — normal tiredness, one-time signals
 *   agotamiento_progresivo — ongoing fatigue patterns, accumulative language
 *   desgaste_profundo     — deep wear; persistent loss of energy, meaning, patience
 *   saturación_crítica    — at breaking point; multiple signals, total weight language
 */

const EXHAUSTION_SIGNALS = [
  // Temporal fatigue — single, situational (weight: 1)
  {
    weight: 1,
    patterns: [
      /(?:esta\s+semana|hoy|últimamente)\s+(?:he\s+estado|estoy)\s+(?:muy\s+)?(?:cansado|agotado|fatigado)/i,
      /fue\s+una\s+semana\s+(?:muy\s+)?(?:difícil|larga|pesada|agotadora)/i,
      /me\s+siento\s+(?:un\s+poco\s+)?(?:cansado|agotado)\s+(?:hoy|esta\s+semana)/i,
    ],
  },
  // Accumulated weight language — "siempre", "nunca termina", "todo el tiempo" (weight: 2)
  {
    weight: 2,
    patterns: [
      /(?:siempre|constantemente|todo\s+el\s+tiempo)\s+(?:estoy|me\s+siento)\s+(?:cansado|agotado|exhausto)/i,
      /(?:nunca\s+(?:termina|acaba|descansa)|no\s+(?:para|termina))\s+(?:esto|el\s+trabajo|el\s+ministerio)/i,
      /(?:no\s+me\s+(?:recupero|repongo)|cada\s+vez\s+más\s+(?:cansado|agotado))/i,
      /(?:el\s+peso\s+(?:del\s+ministerio|de\s+todo\s+esto)|cargar\s+con\s+todo)\s+(?:es|se\s+siente)/i,
    ],
  },
  // Loss of patience and frustration accumulation (weight: 2)
  {
    weight: 2,
    patterns: [
      /(?:ya\s+no\s+tengo\s+(?:paciencia|energía|ganas)|me\s+irrito\s+(?:fácilmente|con\s+facilidad))/i,
      /(?:me\s+(?:cuesta|resulta)\s+(?:mucho\s+)?(?:escuchar|atender|ayudar)|ya\s+no\s+puedo\s+(?:más\s+)?(?:escuchar|sostener))/i,
      /(?:todo\s+me\s+(?:parece|resulta)\s+(?:una\s+carga|pesado|difícil)|siento\s+que\s+todo\s+pesa)/i,
    ],
  },
  // Deep desgaste — meaning fatigue, vocational questioning (weight: 3)
  {
    weight: 3,
    patterns: [
      /(?:me\s+pregunto\s+(?:si|para\s+qué)|¿?para\s+qué\s+(?:sigo|sirve|hago\s+esto))/i,
      /(?:ya\s+no\s+(?:siento|encuentro|veo)\s+(?:el\s+sentido|el\s+propósito|fruto))/i,
      /(?:estoy\s+(?:vacío|vaciado)|me\s+siento\s+(?:sin\s+fuerzas\s+(?:para|de)|completamente\s+agotado))/i,
      /(?:el\s+ministerio\s+(?:me\s+(?:está\s+)?consumiendo|me\s+drena|me\s+agota))/i,
    ],
  },
  // Critical saturation (weight: 4)
  {
    weight: 4,
    patterns: [
      /(?:ya\s+no\s+(?:puedo\s+más|doy\s+más|aguanto)|(?:estoy|me\s+siento)\s+al\s+límite)/i,
      /(?:no\s+puedo\s+(?:más\s+con\s+esto|seguir\s+así)|estoy\s+(?:saturado|colapsado))/i,
      /(?:siento\s+que\s+(?:me\s+voy\s+a\s+quebrar|no\s+aguanto\s+más)|ya\s+(?:no\s+tengo\s+nada\s+que\s+dar|estoy\s+seco))/i,
    ],
  },
];

/**
 * @param {{
 *   text:          string,
 *   emotion:       object|null,
 *   processState:  string,
 *   ministryLoad:  string,
 * }} params
 * @returns {{
 *   state:         string,
 *   score:         number,
 *   instruction:   string|null,
 * }}
 */
export function detectPastoralExhaustion({
  text         = "",
  emotion      = null,
  processState = "apertura",
  ministryLoad = "low",
}) {
  const t = text ?? "";
  let score = 0;

  for (const { weight, patterns } of EXHAUSTION_SIGNALS) {
    if (patterns.some((p) => p.test(t))) score += weight;
  }

  // Emotion amplifiers
  if (emotion?.dominant === "exhaustion" || emotion?.dominant === "discouragement") score += 2;
  if ((emotion?.intensity ?? 0) >= 0.7) score += 1;

  // Process state amplifiers
  if (processState === "agotamiento_espiritual") score += 3;
  if (processState === "resistencia")            score += 1;

  // Ministry load context (from session)
  if (ministryLoad === "critical") score += 1;
  if (ministryLoad === "high")     score += 0.5;

  const state =
    score >= 8 ? "saturación_crítica"      :
    score >= 5 ? "desgaste_profundo"        :
    score >= 3 ? "agotamiento_progresivo"   :
    score >= 1 ? "cansancio_temporal"       :
    "estable";

  return {
    state,
    score: Math.round(score * 10) / 10,
    instruction: _instruction(state),
  };
}

function _instruction(state) {
  if (state === "saturación_crítica") {
    return "Hay señales de saturación pastoral crítica. Responde con máxima suavidad — no exijas, no agrandes, no empujes.";
  }
  if (state === "desgaste_profundo") {
    return "Hay desgaste pastoral profundo. Reduce toda exigencia implícita y acompaña el cansancio con ternura.";
  }
  if (state === "agotamiento_progresivo") {
    return "Hay agotamiento ministerial acumulativo. Favorece el descanso sin culpa, no más carga.";
  }
  if (state === "cansancio_temporal") {
    return "Hay cansancio pastoral presente. Responde con calidez y sin añadir más peso.";
  }
  return null;
}
